import { S25Util } from "./s25-util";

/**
 * Parsed FileStore parameters from a custom attribute value
 */
export interface FileStoreParams {
    objectType: number;
    objectId: number;
    atrbId?: number;
    fileName: string;
}

/**
 * FileStore utility for handling custom attribute file URLs
 * Custom attribute file URLs are stored as query strings like:
 * "object_type=2&object_id=43&atrb_id=-101&file_name=Pickles.jpg"
 */
export class FileStoreUtil {
    private static baseUrl: string = window.ProData && window.ProData.webservicesBaseUrl;

    /**
     * Checks if a value is a FileStore custom attribute URL
     * @param value The custom attribute value to check
     * @returns true if the value contains FileStore parameters
     */
    static isFileStoreUrl(value: any): boolean {
        return (
            typeof value === "string" &&
            !!value.trim() &&
            value.includes("object_type=") &&
            value.includes("file_name=")
        );
    }

    /**
     * Parses FileStore parameters from a custom attribute value
     * @param value The custom attribute value (e.g., "object_type=2&object_id=43&atrb_id=-101&file_name=Pickles.jpg")
     * @returns Parsed parameters or null if invalid
     */
    static parseFileStoreUrl(value: string): FileStoreParams | null {
        if (!FileStoreUtil.isFileStoreUrl(value)) {
            return null;
        }

        try {
            const params = new URLSearchParams(value);
            const objectType = params.get("object_type");
            const objectId = params.get("object_id");
            const fileName = params.get("file_name");
            const atrbId = params.get("atrb_id");

            if (!objectType || !objectId || !fileName) {
                console.warn("FileStore URL missing required parameters:", value);
                return null;
            }

            return {
                objectType: Number(objectType),
                objectId: Number(objectId),
                fileName: fileName,
                atrbId: atrbId ? Number(atrbId) : undefined,
            };
        } catch (error) {
            console.error("Error parsing FileStore URL:", error);
            return null;
        }
    }

    /**
     * Builds the full download URL for a FileStore file
     * @param params FileStore parameters
     * @param display Optional display mode ('inline' or 'attachment')
     * @returns Full URL to download/display the file
     */
    static buildDownloadUrl(params: FileStoreParams, display: "inline" | "attachment" = "attachment"): string {
        const queryParams = new URLSearchParams({
            file_name: params.fileName,
            object_type: params.objectType.toString(),
            object_id: params.objectId.toString(),
            display: display,
        });

        return `${FileStoreUtil.baseUrl}/fs/download/download_file.json?${queryParams.toString()}`;
    }

    /**
     * Convenience method to parse and build download URL in one step
     * @param value The custom attribute value
     * @param display Optional display mode
     * @returns Full download URL or null if invalid
     */
    static getDownloadUrl(value: string, display: "inline" | "attachment" = "attachment"): string | null {
        const params = FileStoreUtil.parseFileStoreUrl(value);
        if (!params) {
            return null;
        }
        return FileStoreUtil.buildDownloadUrl(params, display);
    }

    /**
     * Gets an inline image URL suitable for <img> src attribute
     * @param value The custom attribute value
     * @returns Image URL or null if invalid
     */
    static getImageUrl(value: string): string | null {
        return FileStoreUtil.getDownloadUrl(value, "inline");
    }

    /**
     * Gets the file extension from a FileStore URL
     * @param value The custom attribute value
     * @returns File extension (lowercase) or empty string
     */
    static getFileExtension(value: string): string {
        const params = FileStoreUtil.parseFileStoreUrl(value);
        if (!params || !params.fileName) {
            return "";
        }

        const parts = params.fileName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    /**
     * Checks if a FileStore URL points to an image file
     * @param value The custom attribute value
     * @returns true if the file is an image
     */
    static isImage(value: string): boolean {
        const ext = FileStoreUtil.getFileExtension(value);
        return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
    }
}
